import { useCallback, useEffect, useRef, useState } from "react";
import { FileText, RotateCcw, Sparkles } from "lucide-react";
import { AiIcon } from "./AiIcon";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusPill } from "@/components/patterns";
import { useConfirm } from "@/context/ConfirmContext";
import { useToast } from "@/context/ToastContext";
import { api, unwrapObject } from "@/lib/api";
import { aiFeedback, useAiFeature } from "@/lib/ai";
import { cn } from "@/lib/utils";

export interface CallSummary {
  summary: string;
  customer_needs: string[];
  objections: string[];
  next_step: string | null;
  follow_up_in_days: number | null;
  suggested_outcome: string;
  sentiment: "POSITIVE" | "NEUTRAL" | "NEGATIVE";
}
interface Coaching {
  score: number;
  went_well: string[];
  to_improve: string[];
  questions_asked: number;
  next_step_agreed: boolean;
  talk_ratio_rep: number | null;
}
interface CallAi {
  call_id: string;
  status: "QUEUED" | "TRANSCRIBING" | "SUMMARISING" | "DONE" | "FAILED" | null;
  want_summary: boolean;
  eligible: boolean;
  reason: string | null;
  duration_sec: number;
  estimate_inr: number;
  has_transcript: boolean;
  language_code: string | null;
  segments: { speaker: string; start: number; end: number; text: string }[];
  rep_speaker: string | null;
  summary: CallSummary | null;
  coaching: Coaching | null;
  suggestion_id: string | null;
  error: string | null;
}

const WORKING = ["QUEUED", "TRANSCRIBING", "SUMMARISING"];
const OUTCOME_WORD: Record<string, string> = { INTERESTED: "Interested", CALL_BACK: "Call back later", NOT_INTERESTED: "Not interested", NO_ANSWER: "No answer", BUSY: "Busy", VOICEMAIL: "Voicemail", WRONG_NUMBER: "Wrong number", DO_NOT_CALL: "Asked not to call" };
const SENTIMENT = { POSITIVE: { label: "Positive", tone: "success" }, NEUTRAL: { label: "Neutral", tone: "neutral" }, NEGATIVE: { label: "Negative", tone: "danger" } } as const;
const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

/** One call's AI state, refreshed while it's being worked on (and, if asked, until the recording arrives) */
function useCallAi(callId: string, enabled: boolean, waitForRecording: boolean) {
  const [data, setData] = useState<CallAi | null>(null);
  const tries = useRef(0);
  const load = useCallback(async () => {
    try {
      const d = unwrapObject<CallAi>(await api.get(`/api/Ai/CallSummary/${callId}`));
      setData(d);
      return d;
    } catch {
      return null;
    }
  }, [callId]);
  useEffect(() => {
    if (!enabled) return;
    tries.current = 0;
    setData(null);
    void load();
  }, [enabled, load]);
  // Every few seconds while Sarvam / the AI works; for a fresh call, until its recording is stored (about 3 minutes)
  useEffect(() => {
    if (!enabled || !data) return;
    const working = !!data.status && WORKING.includes(data.status);
    const waiting = waitForRecording && !data.eligible && /no recording/i.test(data.reason || "") && tries.current < 18;
    if (!working && !waiting) return;
    const t = setTimeout(() => {
      tries.current++;
      void load();
    }, working ? 4000 : 10000);
    return () => clearTimeout(t);
  }, [enabled, data, waitForRecording, load]);
  return { data, setData, load };
}

/**
 * Transcript and Summarise for one recorded call, and what came back. Nothing is sent to Sarvam or the AI until the
 * person presses a button (and confirms the cost of a new transcript).
 *   variant "panel": both buttons and Summary / Transcript / Coaching tabs (Calls page)
 *   variant "outcome": Summarise this call, then the summary with "Use this" (the outcome dialog)
 *   variant "link": small links that open the panel in a dialog (timelines)
 */
export function CallAiActions({ callId, variant = "panel", onUse, waitForRecording = false, canRedo = false }: { callId: string; variant?: "panel" | "outcome" | "link"; onUse?: (s: CallSummary) => void; waitForRecording?: boolean; canRedo?: boolean }) {
  const transcriptOn = useAiFeature("CALL_TRANSCRIPT");
  const summaryOn = useAiFeature("CALL_SUMMARY");
  const on = transcriptOn || summaryOn;
  const [open, setOpen] = useState(false);
  const { data, setData } = useCallAi(callId, on && variant !== "link", waitForRecording);
  const { error } = useToast();
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);

  if (!on) return null;
  if (variant === "link") {
    return (
      <>
        <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
          <AiIcon className="size-3.5" /> Transcript &amp; summary
        </button>
        {open && (
          <Dialog open onOpenChange={setOpen}>
            <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle>Call transcript and summary</DialogTitle>
              </DialogHeader>
              <CallAiActions callId={callId} variant="panel" canRedo={canRedo} />
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  }
  if (!data) return null;

  const working = !!data.status && WORKING.includes(data.status);
  const ask = async (kind: "Transcript" | "Summary", redo = false) => {
    // A new transcript is paid for: ask once before starting it
    if (!data.has_transcript) {
      const yes = await confirm({
        title: kind === "Transcript" ? "Get the transcript of this call?" : "Summarise this call?",
        message: kind === "Transcript" ? `The ${mmss(data.duration_sec)} call is written out as text, with the rep and the customer shown apart. It takes a minute or two.` : `The ${mmss(data.duration_sec)} call is written out as text, then the AI writes a short summary and the next step. It takes a minute or two.`,
        confirmLabel: kind === "Transcript" ? "Get transcript" : "Summarise",
      });
      if (!yes) return;
    }
    setBusy(true);
    try {
      setData(unwrapObject<CallAi>(await api.post(`/api/Ai/CallSummary/${callId}/${kind}${redo ? "?redo=1" : ""}`)));
    } catch (e: any) {
      error(kind === "Transcript" ? "Couldn't start the transcript" : "Couldn't start the summary", e.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  if (!data.eligible && !data.has_transcript && !data.summary) {
    if (variant === "outcome" && waitForRecording && /no recording/i.test(data.reason || "")) {
      return <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><AiIcon className="size-3.5" busy /> The recording is on its way; then you can have the AI summarise this call.</p>;
    }
    return variant === "panel" ? <p className="text-xs text-muted-foreground">{data.reason}</p> : null;
  }

  const status = working ? (
    <p className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
      <AiIcon className="size-4" busy /> {data.status === "SUMMARISING" ? "Writing the summary…" : data.want_summary ? "Transcribing, then summarising…" : "Transcribing the call…"} <span className="text-xs">(a minute or two; you'll get a notice)</span>
    </p>
  ) : data.status === "FAILED" ? (
    <p className="text-[13px] text-destructive">{data.error || "It didn't work this time."}</p>
  ) : null;

  if (variant === "outcome") {
    return (
      <div className="space-y-2 rounded-lg border border-primary/25 bg-accent/40 px-3 py-2.5">
        {data.summary ? (
          <SummaryView s={data.summary} compact />
        ) : working ? (
          status
        ) : (
          <>
            {status}
            {summaryOn && (
              <Button type="button" size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={() => void ask("Summary")}>
                <AiIcon className="size-4" busy={busy} /> {data.status === "FAILED" ? "Try again" : "Summarise this call"}
              </Button>
            )}
          </>
        )}
        {data.summary && onUse && (
          <Button
            type="button"
            size="sm"
            className="gap-1.5"
            onClick={() => {
              aiFeedback(data.suggestion_id, true);
              onUse(data.summary!);
            }}
          >
            <Sparkles className="size-3.5" /> Use this
          </Button>
        )}
      </div>
    );
  }

  return <Panel data={data} status={status} busy={busy} transcriptOn={transcriptOn} summaryOn={summaryOn} canRedo={canRedo} ask={ask} />;
}

function Panel({ data, status, busy, transcriptOn, summaryOn, canRedo, ask }: { data: CallAi; status: React.ReactNode; busy: boolean; transcriptOn: boolean; summaryOn: boolean; canRedo: boolean; ask: (k: "Transcript" | "Summary", redo?: boolean) => Promise<void> }) {
  const tabs = [data.summary && "Summary", data.has_transcript && "Transcript", data.coaching && "Coaching"].filter(Boolean) as string[];
  const [tab, setTab] = useState(tabs[0] || "Summary");
  useEffect(() => {
    if (!tabs.includes(tab) && tabs[0]) setTab(tabs[0]);
  }, [tabs.join(), tab]);
  const working = !!data.status && WORKING.includes(data.status);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {transcriptOn && !data.has_transcript && !working && (
          <Button size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={() => void ask("Transcript")}>
            <FileText className="size-3.5" /> {data.status === "FAILED" && !data.want_summary ? "Try again" : "Transcript"}
          </Button>
        )}
        {summaryOn && !data.summary && !working && (
          <Button size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={() => void ask("Summary")}>
            <AiIcon className="size-4" busy={busy} /> {data.status === "FAILED" && data.want_summary ? "Try again" : "Summarise"}
          </Button>
        )}
        {summaryOn && data.summary && canRedo && !working && (
          <Button size="sm" variant="ghost" className="h-8 gap-1.5 text-xs" disabled={busy} onClick={() => void ask("Summary", true)}>
            <RotateCcw className="size-3.5" /> Redo summary
          </Button>
        )}

      </div>
      {status}
      {tabs.length > 0 && (
        <>
          <div role="tablist" className="flex gap-0.5 border-b border-border">
            {tabs.map((t) => (
              <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)} className={cn("h-8 border-b-2 px-3 text-[13px]", tab === t ? "border-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground")}>
                {t}
              </button>
            ))}
          </div>
          {tab === "Summary" && data.summary && <SummaryView s={data.summary} />}
          {tab === "Transcript" && <TranscriptView callId={data.call_id} segments={data.segments} rep={data.rep_speaker} language={data.language_code} />}
          {tab === "Coaching" && data.coaching && <CoachingView c={data.coaching} />}
        </>
      )}
    </div>
  );
}

function SummaryView({ s, compact = false }: { s: CallSummary; compact?: boolean }) {
  const mood = SENTIMENT[s.sentiment] || SENTIMENT.NEUTRAL;
  return (
    <div className="space-y-2 text-[13px]">
      {compact && (
        <div className="flex items-center gap-1.5 text-[11.5px] font-semibold uppercase tracking-wide text-accent-foreground">
          <AiIcon className="size-3.5" /> AI summary of the call
        </div>
      )}
      <p>{s.summary}</p>
      {s.customer_needs.length > 0 && (
        <div>
          <span className="text-xs font-semibold text-muted-foreground">They need</span>
          <ul className="list-disc pl-4">{s.customer_needs.map((n) => <li key={n}>{n}</li>)}</ul>
        </div>
      )}
      {!compact && s.objections.length > 0 && (
        <div>
          <span className="text-xs font-semibold text-muted-foreground">Concerns</span>
          <ul className="list-disc pl-4">{s.objections.map((n) => <li key={n}>{n}</li>)}</ul>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <StatusPill tone="primary">{OUTCOME_WORD[s.suggested_outcome] || s.suggested_outcome}</StatusPill>
        <StatusPill tone={mood.tone}>{mood.label}</StatusPill>
        {s.next_step && <span className="text-muted-foreground">Next: {s.next_step}{s.follow_up_in_days ? ` (in ${s.follow_up_in_days} day${s.follow_up_in_days === 1 ? "" : "s"})` : ""}</span>}
      </div>
    </div>
  );
}

/** The call as text: the rep and the customer in different colours; a line's time plays the recording from there */
function TranscriptView({ callId, segments, rep, language }: { callId: string; segments: CallAi["segments"]; rep: string | null; language: string | null }) {
  if (!segments.length) return <p className="text-xs text-muted-foreground">The transcript was removed with the recording.</p>;
  const who = (s: string) => (rep === null ? `Speaker ${Number(s) + 1}` : s === rep ? "Rep" : "Customer");
  return (
    <div className="space-y-2">
      {language && <p className="text-[11px] text-muted-foreground">Written out automatically, so small mistakes are possible</p>}
      <ol className="max-h-[360px] space-y-1.5 overflow-y-auto pr-1">
        {segments.map((s, i) => {
          const isRep = rep !== null && s.speaker === rep;
          return (
            <li key={i} className={cn("flex gap-2 text-[13px]", isRep && "flex-row-reverse text-right")}>
              <button
                type="button"
                title="Play from here"
                onClick={() => window.dispatchEvent(new CustomEvent("crm:call-seek", { detail: { callId, sec: s.start } }))}
                className="mt-0.5 shrink-0 font-mono text-[11px] text-muted-foreground hover:text-primary"
              >
                {mmss(s.start)}
              </button>
              <span className={cn("max-w-[85%] rounded-xl px-3 py-1.5", isRep ? "bg-accent text-accent-foreground" : "bg-muted")}>
                <span className="block text-[10.5px] font-semibold uppercase tracking-wide opacity-70">{who(s.speaker)}</span>
                {s.text}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function CoachingView({ c }: { c: Coaching }) {
  const tone = c.score >= 8 ? "success" : c.score >= 5 ? "warning" : "danger";
  return (
    <div className="space-y-2.5 text-[13px]">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <StatusPill tone={tone}>Score {c.score}/10</StatusPill>
        {c.talk_ratio_rep !== null && <span className="text-muted-foreground">Rep talked {c.talk_ratio_rep}% of the time</span>}
        <span className="text-muted-foreground">· {c.questions_asked} question{c.questions_asked === 1 ? "" : "s"} asked</span>
        <span className="text-muted-foreground">· {c.next_step_agreed ? "next step agreed" : "no next step agreed"}</span>
      </div>
      {c.went_well.length > 0 && (
        <div>
          <span className="text-xs font-semibold text-success">Went well</span>
          <ul className="list-disc pl-4">{c.went_well.map((n) => <li key={n}>{n}</li>)}</ul>
        </div>
      )}
      {c.to_improve.length > 0 && (
        <div>
          <span className="text-xs font-semibold text-warning">To improve</span>
          <ul className="list-disc pl-4">{c.to_improve.map((n) => <li key={n}>{n}</li>)}</ul>
        </div>
      )}
    </div>
  );
}
